<template>
<div id="customer-page-wrapper" ref="customerPageWrapper" data-dusk="customer-page-wrapper">
  <dd-header-page :header="header">
    <template #tabs>
      <slot name="tabs"></slot>
    </template>
    <template #header-buttons>
      <div v-if="isCategoryPage || isInstancePage" class="flex-row gap-md mt-auto">
        <dd-list-button data-dusk="notes-list-button" :count="notesCount" @click="openNotesModal">
          Notities bekijken
          <template #icon>
            <dd-icon icon="mdi mdi-note-multiple-outline" size="md"/>
          </template>
        </dd-list-button>
        <dd-list-button data-dusk="tasks-list-button" :count="tasksCount" @click="openTasksModal">
          Taken bekijken
          <template #icon>
            <dd-icon icon="mdi mdi-checkbox-multiple-marked-outline" size="md"/>
          </template>
        </dd-list-button>
      </div>
    </template>
    <template #instance-buttons>
      <div class="attachable-actions flex-row gap-sm mb-sm ml-auto mr-xxl ">
        <notes-drawer
            :visible="showDrawer === 'notes'"
            @close="showDrawer = false" />

        <tasks-drawer
            :visible="showDrawer === 'tasks'"
            @close="showDrawer = false" />

        <CreateOrEditNotesModal
            :destroy-on-close="true"
            :note-id="editNoteId"
            @close="() => setShowCreateNoteModal(false)"
            :visible="showCreateNoteModal"
        />

        <ViewNoteModal
            :destroy-on-close="true"
            :note-id="viewNoteId"
            @close="() => setShowViewNoteModal(false)"
            :visible="showViewNoteModal"
            width="60%"
        />

        <template v-if="instance">

          <CreateCalendarEventModal
              @close="showCreateAppointment = false"
              :is-new-event="showCreateAppointment"
              :customer-id="customerId"
              :attachable-key="instance.id"
              :attachable-type="instance.type"
          />

          <dd-task-button
              class="create-appointment-button"
              data-dusk="create-appointment-button"
              @click="createAppointment">
            <template #icon>
              <CalendarPlusOutlineIcon class="color-white icon-size-md"/>
            </template>
            Afspraak maken
          </dd-task-button>

          <dd-task-button
              class="create-letter-button"
              data-dusk="create-letter-button"
              @click="createLetter">
            <template #icon>
              <EmailPlusOutlineIcon class="color-white icon-size-md"/>
            </template>
            Brief aanmaken
          </dd-task-button>

          <dd-task-button
              class="create-email-button"
              data-dusk="create-email-button"
              @click="createEmail">
            <template #icon>
              <AtIcon class="color-white icon-size-md"/>
            </template>
            Email versturen
          </dd-task-button>

          <dd-task-button
              @click="createNote"
              data-dusk="create-note-button"
              class="create-note-button"
          >
            <template #icon>
              <NotePlusOutlineIcon class="color-white icon-size-md"/>
            </template>
            Notitie aanmaken
          </dd-task-button>

          <dd-task-button
              @click="createTask"
              data-dusk="create-task-button"
              class="create-task-button"
          >
            <template #icon>
              <CheckboxMarkedOutlineIcon class="color-white icon-size-md"/>
            </template>
            Taak aanmaken
          </dd-task-button>
        </template>
      </div>
    </template>

      <template #buttons>
        <slot name="buttons"></slot>
      </template>
      <template #content>
        <slot v-if="customerId" name="content"></slot>
      </template>
  </dd-header-page>
</div>
</template>

<script>
import CreateCalendarEventModal
  from "~/modules/calendar_event/components/ModalCreateOrEditComponent.vue";
import CreateOrEditNotesModal from '~/pages/components/notes/CreateOrEditNotesModal.vue'
import ViewNoteModal from '~/pages/components/notes/ViewNoteModal.vue'
import NotesDrawer from '~/pages/components/notes/NotesDrawer.vue';
import store from "./store";
import {mapMutations, mapState, mapActions, mapGetters} from 'vuex';
import TasksDrawer from "~/pages/components/tasks/TasksDrawer.vue";
export default {
  name: "CustomerPage",
  components: {
    CreateCalendarEventModal,
    CreateOrEditNotesModal,
    ViewNoteModal,
    TasksDrawer,
    NotesDrawer,
  },
  beforeCreate() {
    const STORE_KEY = '$_customer_page'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }
  },
  beforeDestroy() {
    this.resetAttachablesCount();
  },
  watch: {
    customerId() {
      this.setCustomerId(this.customerId);
    },
    instance(newInstance, oldInstance) {
      if (newInstance?.id == oldInstance?.id) return;
      this.getAttachablesCount();
    },
    category_id(newCategory, oldCategory) {
      if (newCategory == oldCategory) return
      this.getAttachablesCount();
    },
  },
  created() {
    this.setCustomerId(this.customerId);
    this.getAttachablesCount();
  },

  props: {
    customerId:[Number, String],
    header: {
      required: true,
      type: String
    },
    hasNotes: {
      default: true,
      required: false,
      type: Boolean,
    },
    hasTasks: {
      default: true,
      required: false,
      type: Boolean,
    },
    hasActions: {
      default: true,
      required: false,
      type: Boolean,
    },
    loadingContent: {
      default: false,
      required: false,
      type: Boolean,
    }
  },
  data() {
    return {
      showCreateAppointment: false,
      showDrawer: false,
    }
  },
  computed: {
    hasButtons() {
      return this.$slots.buttons?.length > 0;
    },
    ...mapState({
      instance: 'instance',
      category_id: 'category_id',
    }),
    ...mapState(`$_customer_page`, {
      notesCount: 'notesCount',
      tasksCount: 'tasksCount',
      buttonsExpanded: 'buttonsExpanded',
      showCreateNoteModal: 'showCreateNoteModal',
      showViewNoteModal: 'showViewNoteModal',
      editNoteId: 'editNoteId',
      viewNoteId: 'viewNoteId',
    }),
    ...mapState({
      me: 'me',
    }),
    ...mapGetters({
      isInstancePage: 'isInstancePage',
      isCategoryPage: 'isCategoryPage',
    })
  },
  methods: {
    createNote() {
      this.setShowCreateNoteModal(true);
    },
    createTask() {
      if (this.instance && this.category_id) {
        this.navigator.navigatePath(this.route('vue.task.createForInstance', {
          customer: this.customerId,
          category: this.category_id,
          instanceType: this.instance.type,
          instanceId: this.instance.id,
        }))
      }
      else if (this.category_id) {
        this.navigator.navigatePath(this.route('vue.task.create', {
          customer: this.customerId,
          category: this.category_id
        }));
      }
    },
    createEmail() {
      this.navigator.navigatePath(this.route('vue.customer.email.create.for.instance', {
        category: this.category_id,
        customerId: this.customerId,
        instanceClass: this.instance.class,
        instanceId: this.instance.id,
      }));
    },
    createLetter() {
      this.navigator.navigatePath(this.route('letter.createForInstance', {
        customer: this.customerId,
        category: this.category_id,
        instance: this.instance?.id,
        instanceType: this.instance?.class,
      }));
    },
    createAppointment() {
      // TODO: Load vue modal to create appointment
      this.showCreateAppointment = true;
    },
    ...mapActions(`$_customer_page`, {
      getAttachablesCount: 'getAttachablesCount',
    }),
    ...mapMutations(`$_customer_page`, {
      setCustomerId: 'SET_CUSTOMER_ID',
      resetAttachablesCount: 'RESET_ATTACHABLES_COUNT',
      setShowCreateNoteModal: 'SET_SHOW_CREATE_NOTE_MODAL',
      setShowViewNoteModal: 'SET_SHOW_VIEW_NOTE_MODAL',
    }),
    openTasksModal() {
       this.showDrawer = 'tasks';
    },
    openNotesModal() {
       this.showDrawer = 'notes' ;
    }
  },
}
</script>

<style lang="scss">
@import "~@/_default.scss";

// Enforce white background for all customer pages
#customer-page-wrapper {
  .header-wrapper {
    background-color: white !important;
  }
}

</style>
